import { Injectable } from '@angular/core';
import { WorkspaceRepository } from '@database/central';
import { DataRepository } from '@database/workspace';
import {
	AppVariables,
	ColumnModel,
	DataModel,
	DateTimeService,
	fileBooleanMappings,
	listToDictionaryObject
} from '@shared';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { EntryDetails } from '../../../pages/entry-details/models';

@Injectable()
export class ChildTableService {
	public constructor(
		private dataRepository: DataRepository,
		private workspaceRepository: WorkspaceRepository,
		private dateTimeService: DateTimeService
	) {}

	public getParentEntry$(
		workfileId: string,
		projectNumber: string,
		table: string,
		entryId: number
	): Observable<EntryDetails | any> {
		return this.dataRepository
			.getTableEntry$(table, projectNumber, entryId)
			.pipe(
				switchMap((tableData: DataModel) =>
					this.workspaceRepository
						.getEntityPrimaryKey$(workfileId, table)
						.pipe(
							switchMap((primaryKeys: string[]) =>
								this.dataRepository
									.getColumnsMetadata$(table)
									.pipe(
										switchMap((dataColumns: ColumnModel[]) =>
											this.getTableDetails$(tableData, primaryKeys, dataColumns)
										)
									)
							)
						)
				)
			);
	}

	public getChildEntry$(
		workfileId: string,
		projectNumber: string,
		parentTable: string,
		table: string,
		entryId: number
	): Observable<EntryDetails | any> {
		return this.dataRepository
			.getTableEntry$(table, projectNumber, entryId)
			.pipe(
				switchMap((tableData: DataModel) =>
					this.workspaceRepository
						.getEntityPrimaryKey$(workfileId, parentTable)
						.pipe(
							switchMap((primaryKeys: string[]) =>
								this.dataRepository
									.getColumnsMetadata$(table)
									.pipe(
										switchMap((dataColumns: ColumnModel[]) =>
											this.getTableDetails$(tableData, primaryKeys, dataColumns)
										)
									)
							)
						)
				)
			);
	}

	public getTableDetails$(
		tableData: DataModel,
		primaryKeys: string[],
		dataColumns: ColumnModel[]
	): Observable<EntryDetails | any> {
		const dataVisibleColumns = dataColumns.filter(
			(dataColumn: ColumnModel) => fileBooleanMappings[dataColumn.display_column]
		);

		const dataColumnsDictionary = listToDictionaryObject('column_name', dataVisibleColumns);
		const entityPrimaryKeyData = primaryKeys.every((primaryKey: string) => dataColumnsDictionary[primaryKey]);

		if (!entityPrimaryKeyData) {
			throw new Error('Missing entity primary key metadata!');
		}

		const entryItems = dataVisibleColumns
			.filter((dataColumn: ColumnModel) => !['project_number', primaryKeys].includes(dataColumn.column_name))
			.filter((dataColumn: ColumnModel) => !dataColumnsDictionary[dataColumn.column_label])
			.map((dataColumn: ColumnModel) => ({
				key: dataColumn.column_name,
				value: tableData[dataColumn.column_name],
				label: dataColumn.column_label,
				order: dataColumn.display_order ? dataColumn.display_order : 0,
				valueType: this.dateTimeService.getColumnDataType(dataColumn.edit_style, dataColumn.data_type),
				isMandatory: dataColumn.is_column_nullable
					? !fileBooleanMappings[dataColumn.is_column_nullable]
					: false,
				isLongText:
					!fileBooleanMappings[dataColumn.is_column_nullable] &&
					dataColumn.width &&
					dataColumn.width > AppVariables.LONG_TEXT_LIMIT
						? true
						: false
			}))
			.sort((a, b) => a.order - b.order);

		return of({
			header: primaryKeys.map((item: string) => tableData[item]).join('|'),
			data: this.dateTimeService.formatTimeFields(entryItems)
		});
	}
}
